import React from 'react';
import { View, Text, StyleSheet, Platform, type StyleProp, type ViewStyle, type TextStyle } from 'react-native';
import type { ThemeColors } from '../../../../shared/theme';
import { appTheme } from '../../../../shared/theme';
import { Clickable } from '../../../../components/common/Clickable';
import { SvgIcon } from '../../../../components/common/SvgIcon';
import { dashboardSet1CardShell } from './dashboardSet1Styles';
import { DASH_STUDIO_INNER } from '../../../../shared/theme/studio/dashboardConstants';

const ANDROID_TEXT: TextStyle = Platform.OS === 'android' ? { includeFontPadding: false } : {};

export type DashboardSet1EmptyStateProps = {
  colors: ThemeColors;
  icon: string;
  icon_color?: string;
  title: string;
  message: string;
  cta_label?: string;
  on_cta?: () => void;
  style?: StyleProp<ViewStyle>;
  /** Optional preview slot (e.g. ghost chart bars). */
  preview?: React.ReactNode;
  /** Optional — hide when a section label is rendered above. */
  show_overline?: boolean;
};
export function DashboardSet1EmptyState({
  colors,
  icon,
  icon_color,
  title,
  message,
  cta_label,
  on_cta,
  style,
  preview,
  show_overline = true,
}: DashboardSet1EmptyStateProps) {
  const accent = icon_color ?? colors.primary;

  return (
    <View style={[dashboardSet1CardShell(colors), styles.card, style]}>
      {show_overline ? (
        <Text style={[styles.overline, { color: colors.textLight }, ANDROID_TEXT]} numberOfLines={2}>
          {title}
        </Text>
      ) : null}
      {preview ? <View style={styles.previewSlot}>{preview}</View> : null}
      <View style={styles.body}>
        <View style={[styles.iconRing, { backgroundColor: colors.primaryUltra ?? colors.primaryFade }]}>
          <SvgIcon name={icon} size={26} color={accent} />
        </View>
        <Text style={[styles.message, { color: colors.textSecondary }, ANDROID_TEXT]}>{message}</Text>
        {cta_label && on_cta ? (
          <Clickable
            onPress={on_cta}
            accessibilityRole="button"
            accessibilityLabel={cta_label}
            style={[styles.cta, { backgroundColor: colors.primary }]}
            haptics>
            <Text style={[styles.ctaText, { color: colors.onPrimary ?? colors.white }, ANDROID_TEXT]}>{cta_label}</Text>
          </Clickable>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    paddingHorizontal: DASH_STUDIO_INNER,
    paddingTop: DASH_STUDIO_INNER,
    paddingBottom: DASH_STUDIO_INNER + 4,
    minHeight: 168,
  },
  overline: {
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.9,
    textTransform: 'uppercase',
    marginBottom: appTheme.spacing.md,
  },
  previewSlot: {
    marginBottom: appTheme.spacing.md,
  },
  body: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: appTheme.spacing.md,
    paddingVertical: appTheme.spacing.sm,
  },
  iconRing: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  message: {
    ...appTheme.typography.bodySmall,
    fontSize: 14,
    lineHeight: 21,
    textAlign: 'center',
    maxWidth: 300,
  },
  cta: {
    marginTop: appTheme.spacing.xs,
    paddingVertical: appTheme.spacing.sm + 2,
    paddingHorizontal: appTheme.spacing.xl,
    borderRadius: appTheme.borderRadius.lg,
    alignSelf: 'center',
  },
  ctaText: {
    fontSize: 14,
    fontWeight: '700',
  },
});
